import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Utilities
import bem from '../../../../../lib/bemUtils/bem';
import { createDescribedById } from './utilities';

// Enumerables
import { TooltipPlacement } from '../../../../atoms/Tooltip';

export enum FormLabelModifier {
  // to be found in _form-checkbox.scss
  SWITCH = 'switch',
  CHECKBOX = 'checkbox',
  // to be found in _form-radio.scss
  RADIO = 'radio',
  CHECKBOX_MULTISELECT = 'checkbox-multiselect',
  VERTICAL_ALIGN = 'vertical-align',
  HIDDEN = 'hidden',
}

export enum FormLabelContainerModifier {
  NO_MARGIN = 'no-margin-bottom', // standard for checkboxes
  FLEX100 = 'flex100',
}

// Components
import { Help } from '../../../../../_lib/Form2/partials/Help/Help';
import { RequiredSymbol } from '../../../../../_lib/Form2/partials/RequiredSymbol/RequiredSymbol';

// Enumerables
import { Namespace } from '../../../../../translation/namespace';
import { ExtendedInfoSVG } from '../../../../../_lib/components/ExtendedInfo';
import { SVGIcon } from '../../../../atoms/SVG';

type FormLabelType = {
  id?: string;
  htmlFor: string;
  required?: boolean;
  hidden?: boolean;
  tooltipPlacement?: TooltipPlacement;
  children: ReactNode;
  minLength?: number;
  modifier?: FormLabelModifier | FormLabelModifier[];
  containerModifier?: FormLabelContainerModifier | FormLabelContainerModifier[];
  helpText?: string;
  extendedInfoSVG?: {
    helpText: string;
    svgSpriteID: SVGIcon;
    tooltipPlacement: TooltipPlacement;
  };
  type?: string;
  layout?: 'switch';
};

/**
 * Surrounds it's children with a label.
 * With the property helpText a <a href='#help'>Help</a> is added.
 */
export default function FormLabel({
  id,
  htmlFor,
  required = false,
  hidden = false,
  tooltipPlacement,
  children,
  minLength,
  modifier = [],
  helpText,
  extendedInfoSVG,
  layout,
}: FormLabelType) {
  const { t } = useTranslation();

  const getRequiredSymbol = () => {
    if (required) {
      return <RequiredSymbol />;
    }
    return null;
  };

  // optional: helpText default is empty
  const getFormsHelpText = () => {
    if (helpText) {
      return (
        <Help
          id={createDescribedById(htmlFor)}
          helpText={helpText}
          tooltipPlacement={tooltipPlacement || TooltipPlacement.TOP}
        />
      );
    }
    return null;
  };

  const getExtendedInfoSVG = () => {
    if (extendedInfoSVG) {
      return <ExtendedInfoSVG {...extendedInfoSVG} />;
    }

    return null;
  };

  /**
   * Returns the minLength information values into the label
   * @return {null|*}
   */
  const getLengthConstraintsHelptext = () => {
    if (minLength) {
      return (
        <span className="form-label__length">
          {'\u0020('}
          {minLength && t(`${Namespace.phrases}:minLength`, { minLength })} {t(`${Namespace.phrases}:characters`)})
        </span>
      );
    }

    return null;
  };

  /** Returns the different possible contents
   * - regular children (can be mostly anything)
   * - minLength info
   * - Required symbol
   * @return {*}
   */
  const getLabelContent = (): ReactNode => {
    return (
      <>
        {children}
        {getLengthConstraintsHelptext()}
        {getRequiredSymbol()}
        {layout === 'switch' ? (
          <span
            className="form-label__switch"
            aria-hidden="true" // on/off is hidden for screen readers (part of the semantic checkbox)
            data-switch-on={t(`${Namespace.FormLabel}:on`)}
            data-switch-off={t(`${Namespace.FormLabel}:off`)}
          />
        ) : null}
      </>
    );
  };

  /**
   * Determines, if label content must be hidden or not
   * Important in Checkboxes!
   * @return {*}
   */
  const hideLabelContentOrNot = (): ReactNode => {
    if (hidden) {
      return <span className="invisible">{getLabelContent()}</span>;
    }
    return getLabelContent();
  };

  const getModifier = () => {
    if (hidden && modifier && Array.isArray(modifier)) {
      modifier?.push(FormLabelModifier.HIDDEN);
    }
    return modifier;
  };

  return (
    <span className="form-label__container">
      <label id={id} htmlFor={htmlFor} className={bem('form-label', getModifier())}>
        {hideLabelContentOrNot()}
      </label>
      {getFormsHelpText()}
      {getExtendedInfoSVG()}
    </span>
  );
}
